import { useModal } from '@billy/design-system'
import { createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import {
  useFetchBankLineMatchAssociations,
  useFetchBankLineMatchLines,
  useResolveMatchedItems,
} from '@features/bankTransactions/hooks'
import { BankLineSubjectAssociationWithSubject } from '@features/bankTransactions/types/bankLineSubjectAssociationWithSubject'
import { BookkeepingItem } from '@features/reconciliation/types/bookkeepingItem'
import { isCrossCurrencyMatch } from '@features/reconciliation/utils'
import { calculateDifference } from '@features/reconciliation/utils/calculateDifference'
import { validateSuggestion } from '@features/reconciliation/utils/validateSuggestion'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { BankLine, DifferenceType, ReconciliationSuggestion } from '@modules-deprecated/bankReconciliation/types'

import { ModalId } from '../../../../../enums/modalId'
import { getTransactionAmount } from '../elements/BankTransactionDrawer/utils/getTransactionAmount'
import { useSandboxBankAccountByBookkeepingAccountId } from '../hooks/useSandboxBankAccountByBookkeepingAccountId'
import { useSandboxBankLineSuggestion } from '../hooks/useSandboxBankLineSuggestion'
import { useSandboxBankTransactionsFilters } from '../hooks/useSandboxBankTransactionsFilters'
import { useSandboxBookkeepingItems } from '../hooks/useSandboxBookkeepingItems'
import { canMarkBankLineAsPrivate } from '../utils/canMarkBankLineAsPrivate'
import { getBankTransactionsPermissions } from '../utils/getBankTransactionsPermissions'
import { useSandboxModeContext } from './sandboxModeContext'

interface BankTransactionDrawerContextValue {
  accountId?: string
  accountCurrencyIsSameAsOrganizationCurrency: boolean
  hasBankReconciliationAutoMatchScope: boolean
  hasBankReconciliationWriteScope: boolean
  openUpgradeModal: () => void
  isPrivate: boolean
  setIsPrivate: (isPrivate: boolean) => void
  canMarkAsPrivate: boolean
  selectedTransaction: BankLine | null
  currency?: string
  selectTransaction: (transaction: BankLine) => void
  updateSelectedTransaction: (transaction: BankLine) => void
  clearSelection: () => void
  selectedBookkeepingItemIds: Set<string>
  combinedTransactions: Map<string, BankLine>
  existingAssociations: BankLineSubjectAssociationWithSubject[]
  associatedBookkeepingItems: BookkeepingItem[]
  linkedBankLines: BankLine[]
  isMatchedItemsLoading: boolean
  toggleBookkeepingItemSelection: (id: string) => void
  toggleCombinedTransactionsSelection: (transaction: BankLine) => void
  selectBookkeepingItem: (id: string) => void
  selectCreatedBookkeepingItem: (id: string) => void
  hasCreatedBookkeepingItemSelected: boolean
  clearAllItems: () => void
  showDifferenceSelector: boolean
  differenceType: DifferenceType | null
  setDifferenceType: (type: DifferenceType | null) => void
  selectedBookkeepingItems: BookkeepingItem[]
  difference: number
  hasDifference: boolean
  isCrossCurrency: boolean
  matchError: string | null
  setMatchError: (error: string | null) => void
  suggestedBookkeepingItem?: BookkeepingItem
  matchSuggestion?: ReconciliationSuggestion
  isSuggestionLoading: boolean
  isMatchedStateLoading: boolean
  isDrawerCloseBlocked: boolean
  setDrawerCloseBlocked: (closeBlocked: boolean) => void
}

const BankTransactionDrawerContext = createContext<BankTransactionDrawerContextValue | undefined>(undefined)

interface BankTransactionDrawerProviderProps {
  children: ReactNode
}

export const BankTransactionDrawerProvider = ({ children }: BankTransactionDrawerProviderProps): ReactElement => {
  const sandbox = useSandboxModeContext()
  const [filters] = useSandboxBankTransactionsFilters()
  const { currency } = useSandboxBankAccountByBookkeepingAccountId(filters.accountId)
  const { organization, isLoading: isLoadingOrganization } = useUserOrganization()
  const { hasBankReconciliationWriteScope, hasBankReconciliationAutoMatchScope } = getBankTransactionsPermissions()
  const { open: openUpgradeModal } = useModal(ModalId.UpgradeSubscriptionModal)

  const [selectedTransaction, setSelectedTransaction] = useState<BankLine | null>(null)
  const [selectedBookkeepingItemIds, setSelectedBookkeepingItemIds] = useState<Set<string>>(new Set())
  const [createdBookkeepingItemIds, setCreatedBookkeepingItemIds] = useState<Set<string>>(new Set())
  const [combinedTransactions, setCombinedTransactions] = useState<Map<string, BankLine>>(new Map())
  const [differenceType, setDifferenceType] = useState<DifferenceType | null>(null)
  const [matchError, setMatchError] = useState<string | null>(null)
  const [isPrivate, setIsPrivate] = useState(false)

  const accountCurrencyIsSameAsOrganizationCurrency = currency === organization?.baseCurrencyId
  const canMarkAsPrivate = canMarkBankLineAsPrivate(organization) && !isLoadingOrganization

  const { items: bookkeepingItems } = useSandboxBookkeepingItems({
    accountId: filters.accountId,
  })

  const { suggestion: matchSuggestion, isLoading: isSuggestionLoading } = useSandboxBankLineSuggestion({
    accountId: filters.accountId,
    bankLine: selectedTransaction ?? undefined,
    bookkeepingItems,
    enabled: hasBankReconciliationAutoMatchScope,
    organizationId: organization?.id,
  })

  const { associations, isLoading: isLoadingAssociations } = useFetchBankLineMatchAssociations({
    matchId: selectedTransaction?.match?.id,
    enabled: !sandbox,
  })

  const { linkedBankLines, isLoading: isLoadingLinkedBankLines } = useFetchBankLineMatchLines({
    matchId: selectedTransaction?.match?.isApproved ? selectedTransaction.match.id : undefined,
    currentBankLineId: selectedTransaction?.id,
    enabled: !sandbox,
  })

  const existingAssociations = useMemo(() => {
    if (isLoadingAssociations) {
      return []
    }
    return associations
  }, [associations, isLoadingAssociations])

  // Validate AI suggestion against fresh bookkeeping items and return the matching item
  const suggestedBookkeepingItem: BookkeepingItem | undefined = useMemo(
    () => validateSuggestion(matchSuggestion, bookkeepingItems),
    [matchSuggestion, bookkeepingItems],
  )

  // For approved matches, resolve each posting back to the original bill/invoice it paid,
  // falling back to the posting itself for daybook/manual entries.
  const { items: associatedBookkeepingItems, isLoading: isMatchedItemsLoading } = useResolveMatchedItems({
    associations,
    enabled: !sandbox && selectedTransaction?.match?.isApproved === true,
    matchId: selectedTransaction?.match?.id,
    bankLineId: selectedTransaction?.id,
    bankLineAmount: selectedTransaction?.amount,
  })
  const resolvedAssociatedBookkeepingItems = useMemo(
    () =>
      sandbox
        ? (sandbox.sandboxState.matchedItemsByMatchId[selectedTransaction?.match?.id ?? ''] ?? [])
        : associatedBookkeepingItems,
    [associatedBookkeepingItems, sandbox, selectedTransaction?.match?.id],
  )
  const resolvedIsMatchedItemsLoading = sandbox ? false : isMatchedItemsLoading

  const resetUIState = useCallback(() => {
    setDifferenceType(null)
    setMatchError(null)
  }, [])

  const selectTransaction = useCallback(
    (transaction: BankLine) => {
      setSelectedTransaction(transaction)
      setSelectedBookkeepingItemIds(new Set())
      setCreatedBookkeepingItemIds(new Set())
      setCombinedTransactions(new Map())
      setIsPrivate(false)
      resetUIState()
    },
    [resetUIState],
  )

  const updateSelectedTransaction = useCallback((transaction: BankLine) => {
    setSelectedTransaction(transaction)
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedTransaction(null)
    setSelectedBookkeepingItemIds(new Set())
    setCreatedBookkeepingItemIds(new Set())
    setCombinedTransactions(new Map())
    setIsPrivate(false)
    resetUIState()
  }, [resetUIState])

  const toggleBookkeepingItemSelection = useCallback(
    (id: string) => {
      setSelectedBookkeepingItemIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) {
          next.delete(id)
        } else {
          next.add(id)
        }
        return next
      })
      resetUIState()
    },
    [resetUIState],
  )

  const toggleCombinedTransactionsSelection = useCallback(
    (transaction: BankLine) => {
      setCombinedTransactions((prev) => {
        const next = new Map(prev)
        if (next.has(transaction.id)) {
          next.delete(transaction.id)
        } else {
          next.set(transaction.id, transaction)
        }
        return next
      })
      resetUIState()
    },
    [resetUIState],
  )

  const clearAllItems = useCallback(() => {
    setSelectedBookkeepingItemIds(new Set())
    setCreatedBookkeepingItemIds(new Set())
    setCombinedTransactions(new Map())
    resetUIState()
  }, [resetUIState])

  const selectBookkeepingItem = useCallback(
    (id: string) => {
      setSelectedBookkeepingItemIds(new Set([id]))
      resetUIState()
    },
    [resetUIState],
  )

  const selectCreatedBookkeepingItem = useCallback(
    (id: string) => {
      setCreatedBookkeepingItemIds((prev) => new Set(prev).add(id))
      selectBookkeepingItem(id)
    },
    [selectBookkeepingItem],
  )

  const hasCreatedBookkeepingItemSelected = useMemo(
    () => Array.from(selectedBookkeepingItemIds).some((id) => createdBookkeepingItemIds.has(id)),
    [selectedBookkeepingItemIds, createdBookkeepingItemIds],
  )

  const selectedBookkeepingItems = useMemo(() => {
    return bookkeepingItems.filter((item) => selectedBookkeepingItemIds.has(item.id))
  }, [bookkeepingItems, selectedBookkeepingItemIds])

  const matchingBookkeepingItems = useMemo(() => {
    const items = [...selectedBookkeepingItems]

    if (suggestedBookkeepingItem && !selectedBookkeepingItemIds.has(suggestedBookkeepingItem.id)) {
      items.push(suggestedBookkeepingItem)
    }

    return items
  }, [selectedBookkeepingItems, selectedBookkeepingItemIds, suggestedBookkeepingItem])

  const difference = useMemo(() => {
    if (!selectedTransaction || matchingBookkeepingItems.length === 0) {
      return 0
    }

    const total = getTransactionAmount(selectedTransaction, Array.from(combinedTransactions.values()))

    return calculateDifference({ ...selectedTransaction, amount: total }, matchingBookkeepingItems, currency)
  }, [selectedTransaction, matchingBookkeepingItems, combinedTransactions, currency])

  const [isDrawerCloseBlocked, setIsDrawerCloseBlocked] = useState(false)
  const setDrawerCloseBlocked = useCallback((closeBlocked: boolean) => {
    setIsDrawerCloseBlocked(closeBlocked)
  }, [])

  const isCrossCurrency = isCrossCurrencyMatch(matchingBookkeepingItems, currency)
  const hasDifference = Math.round(difference * 100) !== 0
  const showDifferenceSelector = hasDifference && !isCrossCurrency && selectedBookkeepingItems.length > 0

  useEffect(() => {
    if (!showDifferenceSelector) {
      setDifferenceType(null)
    }
  }, [showDifferenceSelector])

  const contextValue = useMemo(
    () => ({
      accountId: filters.accountId,
      selectedTransaction,
      currency,
      accountCurrencyIsSameAsOrganizationCurrency,
      hasBankReconciliationAutoMatchScope,
      hasBankReconciliationWriteScope,
      openUpgradeModal,
      isPrivate,
      setIsPrivate,
      canMarkAsPrivate,
      selectTransaction,
      updateSelectedTransaction,
      clearSelection,
      selectedBookkeepingItemIds,
      existingAssociations,
      associatedBookkeepingItems: resolvedAssociatedBookkeepingItems,
      linkedBankLines,
      isMatchedItemsLoading: resolvedIsMatchedItemsLoading,
      toggleBookkeepingItemSelection,
      selectBookkeepingItem,
      selectCreatedBookkeepingItem,
      hasCreatedBookkeepingItemSelected,
      clearAllItems,
      combinedTransactions,
      toggleCombinedTransactionsSelection,
      showDifferenceSelector,
      differenceType,
      setDifferenceType,
      selectedBookkeepingItems,
      difference,
      hasDifference,
      isCrossCurrency,
      matchError,
      setMatchError,
      suggestedBookkeepingItem,
      matchSuggestion,
      isSuggestionLoading,
      isMatchedStateLoading: resolvedIsMatchedItemsLoading || isLoadingLinkedBankLines,
      isDrawerCloseBlocked,
      setDrawerCloseBlocked,
    }),
    [
      filters.accountId,
      selectedTransaction,
      currency,
      accountCurrencyIsSameAsOrganizationCurrency,
      hasBankReconciliationAutoMatchScope,
      hasBankReconciliationWriteScope,
      openUpgradeModal,
      isPrivate,
      setIsPrivate,
      canMarkAsPrivate,
      selectTransaction,
      updateSelectedTransaction,
      clearSelection,
      selectedBookkeepingItemIds,
      existingAssociations,
      resolvedAssociatedBookkeepingItems,
      linkedBankLines,
      resolvedIsMatchedItemsLoading,
      toggleBookkeepingItemSelection,
      selectBookkeepingItem,
      selectCreatedBookkeepingItem,
      hasCreatedBookkeepingItemSelected,
      clearAllItems,
      combinedTransactions,
      toggleCombinedTransactionsSelection,
      showDifferenceSelector,
      differenceType,
      setDifferenceType,
      selectedBookkeepingItems,
      difference,
      hasDifference,
      isCrossCurrency,
      matchError,
      setMatchError,
      suggestedBookkeepingItem,
      matchSuggestion,
      isSuggestionLoading,
      isLoadingLinkedBankLines,
      isDrawerCloseBlocked,
      setDrawerCloseBlocked,
    ],
  )

  return <BankTransactionDrawerContext.Provider value={contextValue}>{children}</BankTransactionDrawerContext.Provider>
}

export const useBankTransactionDrawer = (): BankTransactionDrawerContextValue => {
  const context = useContext(BankTransactionDrawerContext)

  if (!context) {
    throw new Error('useBankTransactionDrawer must be used within BankTransactionDrawerProvider')
  }

  return context
}
